/**
 * Setup wizard — the "confirm your banks and steps" flow every account walks,
 * social or email. Five steps, one screen each, progress up top, nothing
 * skippable that the server would reject anyway:
 *   1 Banks   — pick from every SA bank (+ custom), choose receiving + savings
 *   2 Payday  — cycle start day (default 25)
 *   3 Import  — optional statement/spreadsheet import to auto-build month one
 *   4 Plan    — monthly or yearly (#418), prices + yearly availability from
 *               GET /billing/status/ (the paywall's source), saved with
 *               POST /billing/interval/; yearly only shows when configured
 *   5 Invite  — optional: share on WhatsApp; inviter + joiner both earn free days (#293)
 *   6 Look    — "Last step": keep it simple or show everything (#476, full by default)
 * NO card is taken here. The 5-day trial is cardless; the plan chosen in step 4
 * is only a preference, and the card is captured on the paywall once the
 * trial lapses, which is also where that plan's subscription starts. Step numbering below still begins at 1 so the later steps keep the
 * indices they always had.
 * Finishes by calling /auth/onboarding/complete/, which re-checks server-side.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import PhoneVerify from '../components/PhoneVerify.jsx';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
// Vault's own prices and revenue are rand (Paystack), whatever the account's currency.
import { fmtZAR as fmtR } from '../money.js';
import ViewChoice from '../components/simple/ViewChoice.jsx';

const STEPS = ['Your banks', 'Payday', 'First month', 'Your plan', 'Invite & earn'];

export default function Onboarding() {
  const { me, reload, logout } = useAuth();
  const nav = useNavigate();
  // Signup no longer has a payment step, so every account — owner included —
  // starts on banks. Step 1 stays the first step so 2/3/4 keep their indices.
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // step 2 — banks
  const [saBanks, setSaBanks] = useState([]);
  const [chosen, setChosen] = useState(['TymeBank', 'Capitec']);
  const [custom, setCustom] = useState('');
  const [primary, setPrimary] = useState('');
  const [savings, setSavings] = useState('');
  // Optional per-bank nickname so a user can name distinct accounts at signup.
  const [nick, setNick] = useState({});
  // step 3 — payday
  const [payday, setPayday] = useState(25);
  const [cycleMode, setCycleMode] = useState('salary');
  // #312 — a cash "money spot" (wallet/safe) the new user can start with; created
  // during onboarding so it is usable with the banks from day one.
  const [cashName, setCashName] = useState('Wallet');
  const [cashAmt, setCashAmt] = useState('');

  // step 4 — plan (#418). Same source as the paywall so the prices always match
  // what Paystack will charge; a failed read falls back to monthly only.
  const [plans, setPlans] = useState(null);
  const [planChoice, setPlanChoice] = useState('monthly');

  useEffect(() => {
    api('/budget/banks/options_sa/').then((d) => setSaBanks(d.banks)).catch(() => {});
    api('/billing/status/')
      .then((d) => {
        setPlans(d);
        if (d.billing_interval === 'yearly' && d.yearly_available) setPlanChoice('yearly');
      })
      .catch(() => setPlans({ price_monthly_zar: 59, price_yearly_zar: 590, yearly_available: false }));
  }, []);

  /** Record the plan preference (never charges) and move on to Invite. With
   * yearly not configured there is nothing to choose, so nothing is posted. */
  const savePlan = async () => {
    setError('');
    const interval = plans?.yearly_available ? planChoice : 'monthly';
    if (interval !== (plans?.billing_interval || 'monthly')) {
      setBusy(true);
      try {
        await api('/billing/interval/', { method: 'POST', body: { interval } });
      } catch (e) {
        setError(e.data?.detail || 'Could not save your plan — you can change it later on Account.');
        setBusy(false);
        return;
      }
      setBusy(false);
    }
    setStep(5);
  };

  const toggleBank = (name) =>
    setChosen((c) => (c.includes(name) ? c.filter((x) => x !== name) : [...c, name]));

  const saveBanksAndPayday = async () => {
    setBusy(true); setError('');
    try {
      await api('/budget/onboarding/', {
        method: 'POST',
        body: { banks: chosen.map((n) => ({ name: n, account_name: (nick[n] || '').trim() })),
                primary_bank: primary || chosen[0],
                savings_bank: savings || chosen[1] || chosen[0],
                cycle_start_day: payday, cycle_mode: cycleMode },
      });
      // #312 — create the starting cash spot (best-effort; onboarding never fails
      // on it). Seed it as cash-on-hand (from a person, not a bank) so no bank
      // balance is disturbed; it is fully usable with banks afterwards.
      if (cashName.trim()) {
        try {
          const spot = await api('/budget/money-spots/', { method: 'POST', body: { name: cashName.trim() } });
          const amt = Number(cashAmt);
          if (spot?.id && amt > 0) {
            await api(`/budget/money-spots/${spot.id}/receive/`, { method: 'POST',
              body: { amount: amt, source: 'person', person: 'Starting cash' } });
          }
        } catch { /* a spot is optional at signup */ }
      }
      setStep(3);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  // #476 — the last step asks how Vault should look; full view unless they pick simple.
  const finish = async (view = 'full') => {
    setBusy(true); setError('');
    try {
      if (view === 'simple') await api('/auth/me/', { method: 'PATCH', body: { view_mode: 'simple' } });
      await api('/auth/onboarding/complete/', { method: 'POST' });
      await reload();
      nav('/app');
    } catch (e) {
      setError(e.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  // Invite step (#293) — surface the existing referral link in onboarding so a
  // new user can invite friends by WhatsApp for free days (both sides). Optional;
  // if the account has no code yet it points them to Account instead.
  const refLink = me?.referral_code
    ? `${window.location.origin}/?ref=${me.referral_code}` : '';
  const perRef = me?.referral_bonus_per_referral || 5;
  const maxBonus = me?.referral_max_bonus_days || 10;
  const welcomeDays = me?.referral_welcome_days || 5;
  const waShareUrl = refLink
    ? `https://wa.me/?text=${encodeURIComponent(
        `I'm budgeting by payday across my banks with Vault — join on my link and we both get free days: ${refLink}`)}`
    : '';

  return (
    <div className="min-h-full max-w-2xl mx-auto px-6 py-10 flex flex-col">
      <div className="flex items-center gap-3 mb-8">
        <img src="/logo.svg" alt="" className="w-8 h-8" />
        <h1 className="text-lg font-bold">Set up your Vault</h1>
        {/* Always a way out — setup must never trap anyone. */}
        <button className="ml-auto text-xs text-slate-500 hover:text-slate-300 transition"
                onClick={logout}>
          Sign out
        </button>
      </div>

      {step < 6 && <ol className="flex gap-2 mb-8">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex-1 text-center text-xs pb-2 border-b-2 ${
            i + 1 === step ? 'border-vpurple text-white' :
            i + 1 < step ? 'border-vblue text-slate-400' : 'border-edge text-slate-600'}`}>
            {s}
          </li>
        ))}
      </ol>}

      {step === 1 && (
        <div className="card">
          <h2 className="font-semibold text-lg">Which banks does your money live in?</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {saBanks.map((b) => (
              <button key={b} onClick={() => toggleBank(b)}
                      className={`px-4 py-2 rounded-xl border transition ${
                        chosen.includes(b) ? 'border-vpurple bg-vpurple/15'
                                           : 'border-edge hover:border-slate-500'}`}>
                {b}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input className="input" placeholder="Another bank…" value={custom}
                   onChange={(e) => setCustom(e.target.value)} />
            <button className="border border-edge rounded-xl px-4"
                    onClick={() => { if (custom.trim()) { toggleBank(custom.trim()); setCustom(''); } }}>
              Add
            </button>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-400">Salary lands in
              <select className="input mt-1" value={primary} onChange={(e) => setPrimary(e.target.value)}>
                <option value="">Choose…</option>
                {chosen.map((b) => <option key={b}>{b}</option>)}
              </select>
            </label>
            <label className="text-sm text-slate-400">Savings live in
              <select className="input mt-1" value={savings} onChange={(e) => setSavings(e.target.value)}>
                <option value="">Choose…</option>
                {chosen.map((b) => <option key={b}>{b}</option>)}
              </select>
            </label>
          </div>
          {chosen.length > 0 && (
            <div className="mt-5">
              <p className="text-sm text-slate-400">Name an account (optional)</p>
              <p className="text-[11px] text-slate-500 mb-2">
                More than one account at the same bank? Give each a short name (e.g. Salary, Savings).
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {chosen.map((b) => (
                  <div key={b} className="flex items-center gap-2">
                    <span className="text-sm text-slate-300 w-28 shrink-0 truncate">{b}</span>
                    <input className="input flex-1" placeholder="Nickname (optional)"
                           value={nick[b] || ''}
                           onChange={(e) => setNick((m) => ({ ...m, [b]: e.target.value }))} />
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-5 rounded-xl border border-edge p-3">
            <p className="text-sm text-slate-300 font-medium">Cash you keep on you (optional)</p>
            <p className="text-[11px] text-slate-500 mb-2">
              A wallet, safe or envelope Vault tracks alongside your banks — spend from it,
              or move money in from a bank later.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              <input className="input" placeholder="Wallet" value={cashName}
                     onChange={(e) => setCashName(e.target.value)} />
              <input className="input" inputMode="decimal" placeholder="Amount on hand (optional)"
                     value={cashAmt} onChange={(e) => setCashAmt(e.target.value)} />
            </div>
          </div>
          <button className="btn-primary mt-5" disabled={!chosen.length || !primary || !savings}
                  onClick={() => setStep(2)}>Continue</button>
        </div>
      )}

      {step === 2 && (
        <div className="card">
          <h2 className="font-semibold text-lg">When does a new month start?</h2>
          <div className="mt-4 grid gap-3">
            <button onClick={() => setCycleMode('salary')}
                    className={`text-left p-4 rounded-xl border transition ${
                      cycleMode === 'salary' ? 'border-vpurple bg-vpurple/10' : 'border-edge'}`}>
              <span className="font-semibold">When my salary lands (recommended)</span>
              <span className="block text-sm text-slate-400 mt-1">
                The new month — with fresh recurring expenses — only starts when you tick
                your salary as received. Paid late? Your budget waits with you.
              </span>
            </button>
            <button onClick={() => setCycleMode('day')}
                    className={`text-left p-4 rounded-xl border transition ${
                      cycleMode === 'day' ? 'border-vpurple bg-vpurple/10' : 'border-edge'}`}>
              <span className="font-semibold">On a fixed day each month</span>
              <span className="block text-sm text-slate-400 mt-1">
                The month rolls over on the day you pick, salary in or not.
              </span>
            </button>
          </div>
          <p className="mt-5 text-slate-400 text-sm">
            Which day does your salary usually land? (Also used to trim statement imports
            to the current cycle.)
          </p>
          <div className="mt-2 flex items-center gap-4">
            <input type="range" min="1" max="28" value={payday}
                   onChange={(e) => setPayday(Number(e.target.value))} className="flex-1" />
            <div className="text-3xl font-bold w-16 text-center bg-vault-gradient bg-clip-text text-transparent">
              {payday}<span className="text-base">th</span>
            </div>
          </div>
          <button className="btn-primary mt-5" onClick={saveBanksAndPayday} disabled={busy}>
            {busy ? 'Saving…' : 'Continue'}
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="card">
          <h2 className="font-semibold text-lg">Build your first month</h2>
          <p className="mt-2 text-slate-400">
            The fastest start: import a bank statement or your budget spreadsheet and
            Vault builds this month for you — items, banks, what's already paid. You can
            also start empty and add things as you go.
          </p>
          <div className="mt-5 grid gap-3">
            <button className="btn-primary" onClick={() => setStep(4)} disabled={busy}>
              Continue
            </button>
          </div>
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        </div>
      )}

      {step === 4 && (() => {
        const monthly = plans?.price_monthly_zar ?? 59;
        const yearly = plans?.price_yearly_zar ?? 590;
        const yearlyOn = !!plans?.yearly_available;
        const saveZar = Math.max(0, monthly * 12 - yearly);
        const chosen = yearlyOn ? planChoice : 'monthly';
        return (
          <div className="card" data-guide="onboarding-plan">
            <h2 className="font-semibold text-lg">Choose your plan</h2>
            <p className="mt-2 text-slate-400">
              No card is taken now — your 5-day trial is free and needs no card.
              When the trial ends you add your card once and the plan you choose here
              is charged then. You can change it on your Account page until then.
            </p>
            <div className={`mt-5 grid gap-2 ${yearlyOn ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {[['monthly', fmtR(monthly), 'per month'],
                ...(yearlyOn ? [['yearly', fmtR(yearly), 'per year']] : [])].map(([k, big, sub]) => (
                <button key={k} type="button" onClick={() => setPlanChoice(k)}
                        className={`rounded-xl border p-3 text-left transition ${
                          chosen === k ? 'border-vblue bg-vblue/10' : 'border-edge hover:border-slate-500'}`}>
                  <div className="text-xs uppercase tracking-wide text-slate-400">{k === 'monthly' ? 'Monthly' : 'Yearly'}</div>
                  <div className="text-lg font-bold">{big}</div>
                  <div className="text-xs text-slate-400">{sub}</div>
                  {k === 'yearly' && saveZar > 0 && (
                    <div className="text-[11px] text-emerald-400 mt-1">Save {fmtR(saveZar)} — 2 months free</div>
                  )}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Charged when your trial ends: {chosen === 'yearly' ? `${fmtR(yearly)} a year` : `${fmtR(monthly)} a month`}.
              Nothing is charged today.
            </p>
            <button className="btn-primary mt-5" onClick={savePlan} disabled={busy || !plans}>
              {busy ? 'Saving…' : 'Continue'}
            </button>
            {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
          </div>
        );
      })()}

      {step === 5 && (
        <div className="card">
          <h2 className="font-semibold text-lg">Invite friends — you both get free days</h2>
          <p className="mt-2 text-slate-400">
            Share your link on WhatsApp. Every friend who joins gives you {perRef} extra
            free days (up to {maxBonus} in total), and they get {welcomeDays} welcome days too.
          </p>
          {refLink ? (
            <>
              <div className="mt-4 flex gap-2">
                <input className="input flex-1" readOnly value={refLink}
                       onFocus={(e) => e.target.select()} />
                <button className="border border-edge rounded-xl px-4"
                        onClick={() => navigator.clipboard?.writeText(refLink)}>Copy</button>
              </div>
              <a className="btn-primary mt-4 block text-center" href={waShareUrl}
                 target="_blank" rel="noreferrer">Share on WhatsApp</a>
            </>
          ) : (
            <p className="mt-4 text-sm text-slate-500">
              Your invite link will be ready on your Account page after setup.
            </p>
          )}
          {/* #431 — when the WhatsApp code is switched on, the number is
              verified here before setup can finish (the server enforces it too). */}
          {me?.phone_otp_required && !me?.phone_verified && (
            <PhoneVerify initialPhone={me?.whatsapp_number} onVerified={() => reload()} />
          )}
          <button className="mt-4 w-full text-sm text-slate-400 hover:text-slate-200 transition disabled:opacity-40"
                  onClick={() => setStep(6)} disabled={busy || (me?.phone_otp_required && !me?.phone_verified)}>
            Take me in
          </button>
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        </div>
      )}

      {step === 6 && (
        <>
          <ViewChoice busy={busy} onContinue={finish} />
          {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
        </>
      )}

      {error && step < 3 && <p className="text-red-400 text-sm mt-4">{error}</p>}
    </div>
  );
}
